/**
 * DataTable - Reusable table component for SpendPlan module
 * Pixel-perfect implementation matching Figma design specs
 *
 * Features:
 * - Sortable columns
 * - Search with filters
 * - Pagination
 * - Expandable rows
 * - Custom cell renderers
 * - Group by functionality
 * - Action menu per row
 */

// react
import React, { ReactNode, useState, useCallback, useMemo, useEffect } from 'react';

// mui
import {
	alpha,
	Badge,
	Box,
	Checkbox,
	Chip,
	IconButton,
	Link,
	Menu,
	MenuItem,
	Paper,
	Popover,
	Select,
	SelectChangeEvent,
	Stack,
	styled,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	ChevronLeft as IconChevronLeft,
	ChevronRight as IconChevronRight,
	Download as IconDownload,
	FirstPage as IconFirstPage,
	GridView as IconGridView,
	KeyboardArrowDown as IconExpandDown,
	KeyboardArrowRight as IconExpandRight,
	LastPage as IconLastPage,
	MoreVert as IconMore,
	Search as IconSearch,
	TuneRounded as IconTune,
} from '@mui/icons-material';

// components
import { Empty } from '../Empty/Empty';

// i18n
import { useTranslation } from 'react-i18next';

// theme
import { fontOpenSans } from '../../../theme';

// Types

export type SortDirection = 'ASC' | 'DESC';

export interface ColumnDef<T> {
	/** Unique column identifier */
	id: string;
	/** Column header label */
	label: string;
	/** Minimum width in pixels */
	minWidth?: number;
	/** Text alignment */
	align?: 'left' | 'center' | 'right';
	/** Whether column is sortable */
	sortable?: boolean;
	/** Custom cell renderer */
	render?: (row: T, index: number) => ReactNode;
	/** Custom header renderer */
	renderHeader?: () => ReactNode;
	/** Accessor function for getting cell value (used for default rendering) */
	accessor?: (row: T) => string | number | null | undefined;
	/** Custom sort key (defaults to id if not specified) - used when sort key differs from column id */
	sortKey?: string;
}

export interface GroupByOption {
	/** Unique identifier */
	value: string;
	/** Display label */
	label: string;
	/** Optional color for the label (e.g., 'blue', 'green') */
	color?: 'blue' | 'green' | 'orange' | 'purple' | 'pink' | 'default';
}

export interface FilterOption {
	/** Option value */
	value: string | number;
	/** Display label */
	label: string;
}

export interface FilterConfig {
	/** Unique filter key */
	key: string;
	/** Filter label */
	label: string;
	/** Available options */
	options: FilterOption[];
}

export interface TabConfig {
	/** Tab label text */
	label: string;
	/** Tab value/key */
	value: string | number;
	/** Badge count shown next to the label */
	count?: number;
	/** Badge color */
	color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export interface RowAction<T> {
	/** Action label */
	label: string;
	/** Click handler */
	onClick: (row: T) => void;
	/** Icon component */
	icon?: ReactNode;
	/** Whether this action is destructive (renders in red) */
	destructive?: boolean;
	/** Condition to show action */
	show?: (row: T) => boolean;
	/** Whether the action is disabled */
	disabled?: boolean;
	/** Whether to show a divider before this action */
	dividerBefore?: boolean;
}

export interface DataTableProps<T> {
	/** Data rows to display */
	data: T[];
	/** Column definitions */
	columns: ColumnDef<T>[];
	/** Key extractor for row identification */
	getRowKey: (row: T) => string | number;
	/** Whether data is loading */
	loading?: boolean;
	/** Empty state message */
	emptyMessage?: string;
	/** Empty state subtitle/content */
	emptyContent?: string;
	/** Empty state action buttons */
	emptyActions?: {
		children: string;
		onClick: () => void;
		variant?: 'text' | 'contained' | 'outlined';
	}[];
	/** Empty state icon */
	emptyIcon?: ReactNode;

	// === Sorting ===
	/** Current sort column */
	orderBy?: string;
	/** Current sort direction */
	order?: SortDirection;
	/** Sort change handler */
	onSortChange?: (orderBy: string, order: SortDirection) => void;

	// === Pagination ===
	/** Current page (1-indexed) */
	page?: number;
	/** Rows per page */
	rowsPerPage?: number;
	/** Total row count (for server-side pagination) */
	totalCount?: number;
	/** Page change handler */
	onPageChange?: (page: number) => void;
	/** Rows per page change handler */
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	/** Available rows per page options */
	rowsPerPageOptions?: number[];

	// === Search & Filtering ===
	/** Search placeholder text */
	searchPlaceholder?: string;
	/** Current search value */
	searchValue?: string;
	/** Search change handler */
	onSearchChange?: (value: string) => void;
	/** Show filter button */
	showFilters?: boolean;
	/** Filter configurations for dropdown selects */
	filters?: FilterConfig[];
	/** Current filter values */
	filterValues?: Record<string, string | number>;
	/** Filter change handler */
	onFilterChange?: (key: string, value: string | number) => void;

	// === Group By ===
	/** Group by options */
	groupByOptions?: GroupByOption[];
	/** Selected group by values */
	groupByValues?: string[];
	/** Group by change handler */
	onGroupByChange?: (values: string[]) => void;

	// === Row Actions ===
	/** Actions available for each row */
	rowActions?: RowAction<T>[];
	/** Row click handler */
	onRowClick?: (row: T) => void;

	// === Expandable Rows ===
	/** Whether rows are expandable */
	expandable?: boolean;
	/** Toggle expand when clicking a row */
	expandOnRowClick?: boolean;
	/** Expand all rows */
	expandAll?: boolean;
	/** Render expanded content */
	renderExpanded?: (row: T) => ReactNode;
	/** Get children for nested rows */
	getChildren?: (row: T) => T[];

	// === Header Actions ===
	/** Custom actions for header toolbar */
	headerActions?: ReactNode;
	/** Show download button */
	showDownload?: boolean;
	/** Download button click handler */
	onDownloadClick?: () => void;
	/** Show grid view toggle */
	showGridToggle?: boolean;
	/** Grid view toggle click handler */
	onGridToggleClick?: () => void;

	// === Tabs ===
	/** Tab configurations for tabbed data views */
	tabs?: TabConfig[];
	/** Active tab value */
	activeTab?: string | number;
	/** Tab change handler */
	onTabChange?: (value: string | number) => void;

	// === Footer ===
	/** Optional footer content rendered inside the table container (e.g., quick-add row) */
	footer?: ReactNode;
}

// Styled Components - Pixel-perfect Figma match

// Table header cell
const StyledTableHeadCell = styled(TableCell)({
	backgroundColor: '#f9fafb',
	borderBottom: '1px solid #e5e7eb',
	padding: '10px 12px',
	fontFamily: fontOpenSans,
	fontSize: 13,
	fontWeight: 500,
	lineHeight: 1.4,
	letterSpacing: 0,
	color: '#374151',
	whiteSpace: 'nowrap',
	'& .MuiTableSortLabel-root': {
		color: '#374151',
		fontSize: 13,
		fontWeight: 500,
		'&:hover': {
			color: '#111827',
		},
		'&.Mui-active': {
			color: '#111827',
			'& .MuiTableSortLabel-icon': {
				color: '#374151',
				opacity: 1,
			},
		},
	},
});

// Table body cell
const StyledTableBodyCell = styled(TableCell)({
	borderBottom: '1px solid #e5e7eb',
	padding: '12px 12px',
	fontFamily: fontOpenSans,
	fontSize: 14,
	lineHeight: 1.5,
	color: '#111827',
	verticalAlign: 'middle',
	whiteSpace: 'nowrap',
});

// Table row with hover and zebra striping
const StyledTableRow = styled(TableRow, {
	shouldForwardProp: (prop) => prop !== 'even',
})<{ even?: boolean }>(({ even }) => ({
	backgroundColor: even ? '#f9fafb' : '#ffffff',
	'&:last-child td': {
		borderBottom: 0,
	},
	'&:hover': {
		backgroundColor: '#f3f4f6',
	},
	'&.MuiTableRow-root': {
		cursor: 'pointer',
	},
}));

// Pagination select - minimal styling
const StyledPaginationSelect = styled(Select)({
	fontFamily: fontOpenSans,
	fontSize: 13,
	color: '#374151',
	'& .MuiSelect-select': {
		padding: '2px 24px 2px 8px',
		minHeight: 'unset',
	},
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
});

// Pagination icon button
const StyledPaginationButton = styled(IconButton)({
	padding: 4,
	borderRadius: 4,
	color: '#9ca3af',
	'&:hover': {
		backgroundColor: '#f3f4f6',
		color: '#6b7280',
	},
	'&.Mui-disabled': {
		color: '#e5e7eb',
	},
});

// Sub-Components

interface TableToolbarProps {
	searchPlaceholder?: string;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	groupByOptions?: GroupByOption[];
	groupByValues?: string[];
	onGroupByChange?: (values: string[]) => void;
	showFilters?: boolean;
	filters?: FilterConfig[];
	filterValues?: Record<string, string | number>;
	onFilterChange?: (key: string, value: string | number) => void;
	showDownload?: boolean;
	onDownloadClick?: () => void;
	showGridToggle?: boolean;
	onGridToggleClick?: () => void;
	headerActions?: ReactNode;
}

// Get color value for group by label - matches table cell colors
const getGroupByLabelColor = (color?: GroupByOption['color']) => {
	switch (color) {
		case 'blue':
			return '#2563eb'; // Component links
		case 'orange':
			return '#b45309'; // Category links (brownish-orange)
		case 'pink':
			return '#be185d'; // Expense Type (dark pink/magenta)
		case 'green':
			return '#16a34a';
		case 'purple':
			return '#7c3aed';
		default:
			return '#6b7280';
	}
};

const SEARCH_DEBOUNCE_MS = 500;

const TableToolbar = ({
	searchPlaceholder = 'Search',
	searchValue = '',
	onSearchChange,
	groupByOptions = [],
	groupByValues = [],
	onGroupByChange,
	showFilters,
	filters = [],
	filterValues = {},
	onFilterChange,
	showDownload,
	onDownloadClick,
	showGridToggle,
	onGridToggleClick,
	headerActions,
}: TableToolbarProps) => {
	const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
	const filterPopoverOpen = Boolean(filterAnchorEl);

	// Debounced search - local state for immediate input feedback
	const [localSearchValue, setLocalSearchValue] = useState(searchValue);

	// Sync local state when external searchValue changes (e.g., cleared externally)
	useEffect(() => {
		setLocalSearchValue(searchValue);
	}, [searchValue]);

	// Debounce the search callback
	useEffect(() => {
		// Skip if values are already in sync (prevents unnecessary calls)
		if (localSearchValue === searchValue) return;

		const timeoutId = setTimeout(() => {
			onSearchChange?.(localSearchValue);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timeoutId);
	}, [localSearchValue, onSearchChange, searchValue]);

	const handleGroupByToggle = (value: string) => {
		if (!onGroupByChange) return;
		const newValues = groupByValues.includes(value)
			? groupByValues.filter((v) => v !== value)
			: [...groupByValues, value];
		onGroupByChange(newValues);
	};

	const handleOpenFilters = (event: React.MouseEvent<HTMLElement>) => {
		setFilterAnchorEl(event.currentTarget);
	};

	const handleCloseFilters = () => {
		setFilterAnchorEl(null);
	};

	// Calculate active filter count
	const activeFilterCount = useMemo(() => {
		return filters.filter((filter) => {
			const currentValue = filterValues[filter.key];
			const defaultValue = filter.options[0]?.value;
			// Filter is active if value exists and doesn't match the first (default) option
			return currentValue !== undefined && currentValue !== defaultValue;
		}).length;
	}, [filters, filterValues]);

	const handleClearAllFilters = () => {
		filters.forEach((filter) => {
			const defaultValue = filter.options[0]?.value ?? 'ALL';
			onFilterChange?.(filter.key, defaultValue);
		});
	};

	return (
		<Box sx={{ backgroundColor: '#ffffff' }}>
			{/* Row 1: Search + Action Icons */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '8px 16px',
					borderBottom: '1px solid #f3f4f6',
				}}>
				{/* Search Input - Full width, borderless style */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						flex: 1,
						minWidth: 0,
					}}>
					<IconSearch sx={{ color: '#9ca3af', fontSize: 20, flexShrink: 0 }} />
					<Box
						component="input"
						placeholder={searchPlaceholder}
						value={localSearchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setLocalSearchValue(e.target.value)
						}
						sx={{
							border: 'none',
							outline: 'none',
							backgroundColor: 'transparent',
							fontFamily: fontOpenSans,
							fontSize: 14,
							color: '#111827',
							width: '100%',
							padding: '4px 0',
							'&::placeholder': {
								color: '#9ca3af',
							},
						}}
					/>
				</Box>

				{/* Right side - Action Icons */}
				<Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
					{headerActions}

					{showFilters && filters.length > 0 && (
						<>
							<Tooltip title="Filters">
								<Badge
									badgeContent={activeFilterCount}
									color="primary"
									invisible={activeFilterCount === 0}
									sx={{
										'& .MuiBadge-badge': {
											backgroundColor: '#2563eb',
											color: '#ffffff',
											fontSize: 10,
											fontWeight: 600,
											minWidth: 16,
											height: 16,
											padding: '0 4px',
										},
									}}>
									<IconButton
										onClick={handleOpenFilters}
										size="small"
										sx={{
											padding: '6px',
											color: activeFilterCount > 0 ? '#2563eb' : '#6b7280',
											backgroundColor:
												activeFilterCount > 0 ? alpha('#2563eb', 0.1) : 'transparent',
											'&:hover': {
												backgroundColor: activeFilterCount > 0 ? alpha('#2563eb', 0.2) : '#f3f4f6',
											},
										}}>
										<IconTune sx={{ fontSize: 20 }} />
									</IconButton>
								</Badge>
							</Tooltip>
							<Popover
								open={filterPopoverOpen}
								anchorEl={filterAnchorEl}
								onClose={handleCloseFilters}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
								slotProps={{
									paper: {
										sx: {
											mt: 0.5,
											width: 300,
											boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
											borderRadius: '12px',
										},
									},
								}}>
								<Box
									sx={{
										px: 2.5,
										py: 2,
										borderBottom: '1px solid #e5e7eb',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
									}}>
									<Typography
										sx={{
											fontFamily: fontOpenSans,
											fontSize: 14,
											fontWeight: 600,
											color: '#111827',
										}}>
										Filters
									</Typography>
									{activeFilterCount > 0 && (
										<Link
											component="button"
											onClick={handleClearAllFilters}
											underline="hover"
											sx={{
												fontFamily: fontOpenSans,
												fontSize: 13,
												color: '#6b7280',
												cursor: 'pointer',
											}}>
											Clear all
										</Link>
									)}
								</Box>

								<Stack spacing={2.5} sx={{ p: 2.5 }}>
									{filters.map((filter) => (
										<Box key={filter.key}>
											<Typography
												sx={{
													fontFamily: fontOpenSans,
													fontSize: 13,
													fontWeight: 500,
													color: '#374151',
													mb: 1,
												}}>
												{filter.label}
											</Typography>
											<Select
												fullWidth
												size="small"
												value={filterValues[filter.key] ?? filter.options[0]?.value ?? ''}
												onChange={(e: SelectChangeEvent<string | number>) =>
													onFilterChange?.(filter.key, e.target.value)
												}
												sx={{
													fontFamily: fontOpenSans,
													fontSize: 14,
													backgroundColor: '#ffffff',
													'& .MuiSelect-select': {
														py: 1.25,
													},
													'& .MuiOutlinedInput-notchedOutline': {
														borderColor: '#e5e7eb',
													},
													'&:hover .MuiOutlinedInput-notchedOutline': {
														borderColor: '#d1d5db',
													},
												}}>
												{filter.options.map((option) => (
													<MenuItem
														key={option.value}
														value={option.value}
														sx={{
															fontFamily: fontOpenSans,
															fontSize: 14,
														}}>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</Box>
									))}
								</Stack>
							</Popover>
						</>
					)}

					{showDownload && (
						<Tooltip title="Download">
							<IconButton
								onClick={onDownloadClick}
								size="small"
								sx={{
									padding: '6px',
									color: '#6b7280',
									'&:hover': {
										backgroundColor: '#f3f4f6',
									},
								}}>
								<IconDownload sx={{ fontSize: 20 }} />
							</IconButton>
						</Tooltip>
					)}

					{showGridToggle && (
						<Tooltip title="Grid View">
							<IconButton
								onClick={onGridToggleClick}
								size="small"
								sx={{
									padding: '6px',
									color: '#6b7280',
									'&:hover': {
										backgroundColor: '#f3f4f6',
									},
								}}>
								<IconGridView sx={{ fontSize: 20 }} />
							</IconButton>
						</Tooltip>
					)}
				</Stack>
			</Box>

			{/* Row 2: Group By Options */}
			{groupByOptions.length > 0 && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						padding: '6px 16px',
						borderBottom: '1px solid #e5e7eb',
						gap: 2,
					}}>
					<Typography
						sx={{
							fontFamily: fontOpenSans,
							fontSize: 13,
							color: '#6b7280',
						}}>
						Group by
					</Typography>
					{groupByOptions.map((option) => (
						<Box
							key={option.value}
							onClick={() => handleGroupByToggle(option.value)}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								cursor: 'pointer',
								'&:hover': {
									opacity: 0.8,
								},
							}}>
							<Checkbox
								checked={groupByValues.includes(option.value)}
								size="small"
								sx={{
									padding: 0,
									color: '#d1d5db',
									'&.Mui-checked': {
										color: getGroupByLabelColor(option.color),
									},
									'& .MuiSvgIcon-root': {
										fontSize: 18,
									},
								}}
							/>
							<Typography
								sx={{
									fontFamily: fontOpenSans,
									fontSize: 13,
									fontWeight: 600, // Bold
									color: getGroupByLabelColor(option.color),
								}}>
								{option.label}
							</Typography>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

interface TablePaginationBarProps {
	page: number;
	rowsPerPage: number;
	totalCount: number;
	onPageChange?: (page: number) => void;
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	rowsPerPageOptions?: number[];
}

const TablePaginationBar = ({
	page,
	rowsPerPage,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	rowsPerPageOptions = [10, 25, 50, 100],
}: TablePaginationBarProps) => {
	const { t } = useTranslation();
	const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
	const start = totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1;
	const end = Math.min(page * rowsPerPage, totalCount);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end',
				padding: '12px 16px',
				borderTop: '1px solid #e5e7eb',
				backgroundColor: '#ffffff',
				gap: 3,
			}}>
			{/* Rows per page */}
			<Stack direction="row" alignItems="center" spacing={1}>
				<Typography
					sx={{
						fontSize: 13,
						fontFamily: fontOpenSans,
						color: '#6b7280',
					}}>
					{t('rowsPerPage')}
				</Typography>
				<StyledPaginationSelect
					value={rowsPerPage}
					onChange={(e: SelectChangeEvent<unknown>) =>
						onRowsPerPageChange?.(Number(e.target.value))
					}
					size="small"
					variant="outlined">
					{rowsPerPageOptions.map((n) => (
						<MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
							{n}
						</MenuItem>
					))}
				</StyledPaginationSelect>
			</Stack>

			{/* Page info */}
			<Typography
				sx={{
					fontSize: 13,
					fontFamily: fontOpenSans,
					color: '#6b7280',
				}}>
				{start} — {end} of {totalCount}
			</Typography>

			{/* Navigation */}
			<Stack direction="row" alignItems="center" spacing={0.5}>
				<StyledPaginationButton onClick={() => onPageChange?.(1)} disabled={page <= 1} size="small">
					<IconFirstPage sx={{ fontSize: 20 }} />
				</StyledPaginationButton>
				<StyledPaginationButton
					onClick={() => onPageChange?.(page - 1)}
					disabled={page <= 1}
					size="small">
					<IconChevronLeft sx={{ fontSize: 20 }} />
				</StyledPaginationButton>
				<StyledPaginationButton
					onClick={() => onPageChange?.(page + 1)}
					disabled={page >= totalPages}
					size="small">
					<IconChevronRight sx={{ fontSize: 20 }} />
				</StyledPaginationButton>
				<StyledPaginationButton
					onClick={() => onPageChange?.(totalPages)}
					disabled={page >= totalPages}
					size="small">
					<IconLastPage sx={{ fontSize: 20 }} />
				</StyledPaginationButton>
			</Stack>
		</Box>
	);
};

// Main Component

export function DataTable<T>({
	data,
	columns,
	getRowKey,
	loading = false,
	emptyMessage = 'No data available',
	emptyContent,
	emptyActions,
	emptyIcon,
	orderBy,
	order = 'ASC',
	onSortChange,
	page = 1,
	rowsPerPage = 10,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	rowsPerPageOptions = [10, 25, 50, 100],
	searchPlaceholder,
	searchValue,
	onSearchChange,
	showFilters,
	filters,
	filterValues,
	onFilterChange,
	groupByOptions,
	groupByValues,
	onGroupByChange,
	rowActions,
	onRowClick,
	expandable,
	expandOnRowClick,
	expandAll,
	renderExpanded,
	getChildren,
	headerActions,
	showDownload,
	onDownloadClick,
	showGridToggle,
	onGridToggleClick,
	tabs,
	activeTab,
	onTabChange,
	footer,
}: DataTableProps<T>) {
	// State
	const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [menuRow, setMenuRow] = useState<T | null>(null);

	// Sync expandAll prop to expandedRows
	useEffect(() => {
		if (expandAll === true) {
			setExpandedRows(new Set(data.map((row) => getRowKey(row))));
		} else if (expandAll === false) {
			setExpandedRows(new Set());
		}
	}, [expandAll, data, getRowKey]);

	// Handlers
	const handleRequestSort = useCallback(
		(columnId: string) => {
			if (!onSortChange) return;
			const isAsc = orderBy === columnId && order === 'ASC';
			onSortChange(columnId, isAsc ? 'DESC' : 'ASC');
		},
		[orderBy, order, onSortChange]
	);

	const handleToggleExpand = useCallback((rowKey: string | number, event: React.MouseEvent) => {
		event.stopPropagation();
		setExpandedRows((prev) => {
			const next = new Set(prev);
			if (next.has(rowKey)) {
				next.delete(rowKey);
			} else {
				next.add(rowKey);
			}
			return next;
		});
	}, []);

	const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, row: T) => {
		event.stopPropagation();
		setMenuAnchor(event.currentTarget);
		setMenuRow(row);
	}, []);

	const handleMenuClose = useCallback(() => {
		setMenuAnchor(null);
		setMenuRow(null);
	}, []);

	const handleRowClick = useCallback(
		(row: T) => {
			if (expandOnRowClick && expandable && getRowKey) {
				const rowKey = getRowKey(row);
				setExpandedRows((prev) => {
					const next = new Set(prev);
					if (next.has(rowKey)) {
						next.delete(rowKey);
					} else {
						next.add(rowKey);
					}
					return next;
				});
			}
			if (onRowClick) {
				onRowClick(row);
			}
		},
		[onRowClick, expandOnRowClick, expandable, getRowKey]
	);

	// Computed values
	const actualTotalCount = totalCount ?? data.length;
	const hasRowActions = rowActions && rowActions.length > 0;
	const showToolbar =
		onSearchChange ||
		(groupByOptions && groupByOptions.length > 0) ||
		showFilters ||
		showDownload ||
		showGridToggle ||
		headerActions;

	// Visible actions for current menu row
	const visibleActions = useMemo(() => {
		if (!rowActions || !menuRow) return [];
		return rowActions.filter((action) => !action.show || action.show(menuRow));
	}, [rowActions, menuRow]);

	// Render cell content
	const renderCellContent = useCallback(
		(column: ColumnDef<T>, row: T, index: number): ReactNode => {
			if (column.render) {
				return column.render(row, index);
			}
			if (column.accessor) {
				const value = column.accessor(row);
				return value ?? '----';
			}
			return '----';
		},
		[]
	);

	// Render a single row (recursive for nested rows)
	const renderRow = (row: T, index: number, depth = 0): ReactNode => {
		const rowKey = getRowKey(row);
		const isExpanded = expandedRows.has(rowKey);
		const children = getChildren?.(row) ?? [];
		const hasChildren = children.length > 0;
		const canExpand = expandable && (hasChildren || renderExpanded);
		const isEven = index % 2 === 1; // 0-indexed, so odd indices are even rows visually

		return (
			<React.Fragment key={rowKey}>
				<StyledTableRow
					even={isEven}
					onClick={() => handleRowClick(row)}
					sx={{
						cursor: onRowClick || expandOnRowClick ? 'pointer' : 'default',
					}}>
					{/* Expand toggle column */}
					{expandable && (
						<StyledTableBodyCell
							sx={{
								width: 32,
								padding: '12px 4px 12px 12px',
								verticalAlign: 'middle',
							}}>
							{canExpand ? (
								<IconButton
									size="small"
									onClick={(e) => handleToggleExpand(rowKey, e)}
									sx={{
										padding: 0,
										color: '#9ca3af',
										'&:hover': {
											color: '#6b7280',
											backgroundColor: 'transparent',
										},
									}}>
									{isExpanded ? (
										<IconExpandDown sx={{ fontSize: 20 }} />
									) : (
										<IconExpandRight sx={{ fontSize: 20 }} />
									)}
								</IconButton>
							) : (
								<Box sx={{ width: 20, height: 20 }} />
							)}
						</StyledTableBodyCell>
					)}

					{/* Data columns */}
					{columns.map((column) => (
						<StyledTableBodyCell
							key={column.id}
							align={column.align}
							sx={{
								minWidth: column.minWidth,
								paddingLeft:
									column.id === columns[0]?.id && !expandable ? `${12 + depth * 24}px` : undefined,
							}}>
							{renderCellContent(column, row, index)}
						</StyledTableBodyCell>
					))}

					{/* Actions column */}
					{hasRowActions && (
						<StyledTableBodyCell
							align="right"
							sx={{ width: 40, padding: '12px 12px 12px 4px', verticalAlign: 'top' }}
							onClick={(e) => e.stopPropagation()}>
							<IconButton
								size="small"
								onClick={(e) => handleMenuOpen(e, row)}
								sx={{
									padding: 0,
									color: '#9ca3af',
									'&:hover': {
										color: '#6b7280',
										backgroundColor: 'transparent',
									},
								}}>
								<IconMore sx={{ fontSize: 20 }} />
							</IconButton>
						</StyledTableBodyCell>
					)}
				</StyledTableRow>

				{/* Expanded content */}
				{isExpanded && renderExpanded && (
					<TableRow>
						<TableCell
							colSpan={columns.length + (expandable ? 1 : 0) + (hasRowActions ? 1 : 0)}
							sx={{ padding: 0, border: 0 }}>
							{renderExpanded(row)}
						</TableCell>
					</TableRow>
				)}

				{/* Nested children */}
				{isExpanded && hasChildren && children.map((child, i) => renderRow(child, i, depth + 1))}
			</React.Fragment>
		);
	};

	return (
		<Paper
			elevation={0}
			sx={{
				border: '1px solid #e5e7eb',
				borderRadius: 2,
				overflow: 'hidden',
				backgroundColor: '#ffffff',
			}}>
			{/* Tabs */}
			{tabs && tabs.length > 0 && (
				<Tabs
					value={activeTab ?? tabs[0]?.value}
					onChange={(_, newValue) => onTabChange?.(newValue)}
					sx={{
						bgcolor: '#f9fafb',
						borderBottom: '1px solid #e5e7eb',
						minHeight: 44,
						px: 1,
						'& .MuiTab-root': {
							fontFamily: fontOpenSans,
							fontWeight: 600,
							fontSize: 13,
							minHeight: 44,
							px: 2,
							py: 1,
							textTransform: 'none',
						},
					}}>
					{tabs.map((tab) => (
						<Tab
							key={tab.value}
							value={tab.value}
							label={
								<Stack direction="row" spacing={1} alignItems="center">
									<span>{tab.label}</span>
									{tab.count !== undefined && (
										<Chip
											label={tab.count}
											size="small"
											color={tab.color || 'default'}
											sx={{ fontWeight: 700, height: 22, minWidth: 28 }}
										/>
									)}
								</Stack>
							}
						/>
					))}
				</Tabs>
			)}

			{/* Toolbar */}
			{showToolbar && (
				<TableToolbar
					searchPlaceholder={searchPlaceholder}
					searchValue={searchValue}
					onSearchChange={onSearchChange}
					groupByOptions={groupByOptions}
					groupByValues={groupByValues}
					onGroupByChange={onGroupByChange}
					showFilters={showFilters}
					filters={filters}
					filterValues={filterValues}
					onFilterChange={onFilterChange}
					showDownload={showDownload}
					onDownloadClick={onDownloadClick}
					showGridToggle={showGridToggle}
					onGridToggleClick={onGridToggleClick}
					headerActions={headerActions}
				/>
			)}

			{/* Table */}
			<TableContainer sx={{ overflowX: 'auto' }}>
				<Table sx={{ minWidth: 600 }}>
					<TableHead>
						<TableRow sx={{ backgroundColor: '#f9fafb' }}>
							{/* Expand column header */}
							{expandable && (
								<StyledTableHeadCell sx={{ width: 32, padding: '12px 4px 12px 12px' }} />
							)}

							{/* Data column headers */}
							{columns.map((column) => {
								const sortKeyValue = column.sortKey || column.id;
								return (
									<StyledTableHeadCell
										key={column.id}
										align={column.align}
										sx={{ minWidth: column.minWidth }}>
										{column.sortable !== false && onSortChange ? (
											<TableSortLabel
												active={orderBy === sortKeyValue}
												direction={
													orderBy === sortKeyValue ? (order === 'ASC' ? 'asc' : 'desc') : 'asc'
												}
												onClick={() => handleRequestSort(sortKeyValue)}
												hideSortIcon={orderBy !== sortKeyValue}
												sx={{
													'& .MuiTableSortLabel-icon': {
														opacity: 1,
														color: '#6b7280',
													},
												}}>
												{column.renderHeader ? column.renderHeader() : column.label}
											</TableSortLabel>
										) : column.renderHeader ? (
											column.renderHeader()
										) : (
											column.label
										)}
									</StyledTableHeadCell>
								);
							})}

							{/* Actions column header */}
							{hasRowActions && (
								<StyledTableHeadCell sx={{ width: 40, padding: '12px 12px 12px 4px' }} />
							)}
						</TableRow>
					</TableHead>

					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell
									colSpan={columns.length + (expandable ? 1 : 0) + (hasRowActions ? 1 : 0)}
									sx={{ padding: '48px 16px', textAlign: 'center', border: 0 }}>
									<Empty isLoading title={{ children: '' }} content={{ children: '' }} />
								</TableCell>
							</TableRow>
						) : data.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={columns.length + (expandable ? 1 : 0) + (hasRowActions ? 1 : 0)}
									sx={{ padding: '48px 16px', textAlign: 'center', border: 0 }}>
									<Empty
										IconCircleProps={emptyIcon ? { icon: emptyIcon } : undefined}
										title={{ children: emptyMessage }}
										content={{ children: emptyContent || 'Try adjusting your search or filters' }}
										actions={emptyActions}
									/>
								</TableCell>
							</TableRow>
						) : (
							data.map((row, index) => renderRow(row, index))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pagination */}
			{(onPageChange || onRowsPerPageChange) && (
				<TablePaginationBar
					page={page}
					rowsPerPage={rowsPerPage}
					totalCount={actualTotalCount}
					onPageChange={onPageChange}
					onRowsPerPageChange={onRowsPerPageChange}
					rowsPerPageOptions={rowsPerPageOptions}
				/>
			)}

			{/* Footer */}
			{footer && <Box sx={{ borderTop: '1px solid #e5e7eb' }}>{footer}</Box>}

			{/* Row Actions Menu */}
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				slotProps={{
					paper: {
						sx: {
							minWidth: 160,
							boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
							borderRadius: 1.5,
							border: '1px solid #e5e7eb',
						},
					},
				}}>
				{visibleActions.map((action, index) => (
					<React.Fragment key={index}>
						{action.dividerBefore && <Box sx={{ borderTop: '1px solid #e5e7eb', my: 0.5 }} />}
						<MenuItem
							onClick={() => {
								if (menuRow && !action.disabled) action.onClick(menuRow);
								if (!action.disabled) handleMenuClose();
							}}
							disabled={action.disabled}
							sx={{
								fontSize: 13,
								fontFamily: fontOpenSans,
								color: action.disabled ? '#9ca3af' : action.destructive ? '#dc2626' : '#374151',
								gap: 1.5,
								py: 1,
								'&:hover': {
									backgroundColor: action.disabled
										? 'transparent'
										: action.destructive
											? '#fef2f2'
											: '#f9fafb',
								},
							}}>
							{action.icon}
							{action.label}
						</MenuItem>
					</React.Fragment>
				))}
			</Menu>
		</Paper>
	);
}

export default DataTable;
